import {
  Card,
  CardProps,
  Grid,
  GridProps,
  ListItem as ChakraListItem,
  ListItemProps as ChakraListItemProps,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import {
  DragControls,
  motion,
  MotionProps,
  useMotionValue,
} from "framer-motion";
import { ReactNode } from "react";

import { ReorderListItem } from "@/common/components/ReorderList";
import { useRaisedShadow } from "@/common/hooks/useRaisedShadow";

const MotionGrid = motion<GridProps & CardProps>(Grid);

export type ListLayoutProps = {
  children: ReactNode;
  showAsCard?: boolean;
  showAsHeader?: boolean;
} & GridProps &
  MotionProps;

export function ListLayout({
  children,
  showAsCard = false,
  showAsHeader = false,
  ...props
}: ListLayoutProps) {
  return (
    <MotionGrid
      alignItems="center"
      as={showAsCard ? Card : "div"}
      gridGap={4}
      minH={10}
      px={4}
      py={1}
      variant={showAsCard ? "filled" : undefined}
      layout
      {...(showAsHeader && {
        color: "dimmed",
        fontSize: "sm",
        fontWeight: "medium",
      })}
      {...props}
    >
      {children}
    </MotionGrid>
  );
}

type ListItemProps<T> = Omit<
  ChakraListItemProps,
  "value" | "color" | keyof MotionProps
> & {
  value?: T;
  canDrag?: boolean;
  dragControls?: DragControls;
  children?: ReactNode;
};

export const ListItem = <T,>({
  value,
  canDrag,
  dragControls,
  children,
  ...rest
}: ListItemProps<T>) => {
  const y = useMotionValue(0);
  const boxShadow = useRaisedShadow(y);
  const styles = useMultiStyleConfig("Card", { variant: "filled" });

  const sharedProps = {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 64px",
    alignItems: "center",
    gridGap: 4,
    minH: 10,
    px: 4,
    py: 2,
    ...rest,
  };

  if (canDrag) {
    return (
      <ReorderListItem
        {...sharedProps}
        animate={{ opacity: 1 }}
        dragControls={canDrag ? dragControls : undefined}
        dragListener={canDrag ? false : undefined}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        position="relative"
        style={{ y, boxShadow }}
        sx={styles.container}
        value={value}
      >
        {children}
      </ReorderListItem>
    );
  } else {
    return <ChakraListItem {...sharedProps}>{children}</ChakraListItem>;
  }
};
